/**
 * Multi-add, before anything is saved (ENG-4940 + ENG-4942).
 *
 * Rows of a multi-add skip the brand/model step, so the two jobs that step does
 * for a single camera happen here, in order, when the user clicks "Add N
 * Sources":
 *
 *   1. ENG-4940 — identify each selected camera discovery has no model for, by
 *      asking its Bridge to probe that one camera's address (`identifyAtHost`,
 *      the same bounded wait as the RTSP step). There is no manual pick on this
 *      path, so a hit always applies. Every probe runs at once, so the whole
 *      step waits at most one window.
 *   2. ENG-4942 — list the cameras that will be saved on the generic driver, for
 *      the one "uses basic controls" dialog shown before the save.
 *
 * Pure apart from the injected `identify` / `fetchTemplate`, so the rules are
 * unit-tested without React, the broker or the registry.
 */
import type { Agent } from '@studio/models/agent';
import type { Brand, Model } from '@studio/models/device';
import type { T_Maybe } from '@tryiris-ai/js-types';
import type { Collection } from '#~/api/device/registry/index.js';
import type { Template } from '#~/device/registry/index.js';
import { cameraHost } from '../source/discovery-transports.js';
import { buildBulkCandidate } from '../source/utils.js';
import type { Discovery } from '#~/device/discovery/index.js';
import { basicControlsCameraLabel } from './utils.js';

/**
 * What an identify answer contributes: the registry model (always) and brand
 * (when the match named one). Structurally `IdentifiedDiscovery`
 * (identify-at-host.ts), declared here so this module — which the wiring
 * context's types read — does not import the live Bridge-state module.
 */
export type IdentifiedCamera = Readonly<{
  modelId: Model['id'];
  brandId?: Brand['id'] | undefined;
}>;

/** Templates by model id, as the selector mirrors them (`source.bulkTemplates`). */
export type BulkTemplates = Record<Model['id'], T_Maybe<Template>>;

/** Brand-detected fallbacks by brand id, as the selector mirrors them (`source.bulkBrandDetected`). */
export type BulkBrandDetected = Record<
  Brand['id'],
  T_Maybe<{ otherModelId: Model['id']; template: T_Maybe<Template> }>
>;

/** One camera the multi-add "uses basic controls" dialog lists. */
export type BulkBasicControlsCamera = Readonly<{
  /** The discovery row, for the list key and STag. */
  id: Discovery['id'];
  /** The row's own name, as the discovery table showed it. */
  name: string;
  /** "Brand Model", "Brand camera" or "This camera" (`basicControlsCameraLabel`). */
  label: string;
  /** Registry brand name reported on `device_basic_controls_closed` ('' when unknown). */
  brandName: string;
  /** Registry model name reported on `device_basic_controls_closed` ('' when unknown). */
  modelName: string;
  /** Recognized, but the registry marks it not yet supported (ENG-4924). */
  notYetSupported: boolean;
}>;

/** What the bulk save needs once the identify step settled. */
export type BulkSavePrepared = Readonly<{
  /**
   * The click-time selection. The save uses this, not the live selection: the
   * table stays clickable during the identify wait, and a row ticked or
   * unticked then must not be saved without being listed (or listed without
   * being saved).
   */
  selectionIds: readonly Discovery['id'][];
  /** The click-time rows, with identified models applied. */
  discoveries: readonly Discovery[];
  /** The click-time templates, plus any fetched for a selected model. */
  templates: BulkTemplates;
  /** Cameras the "uses basic controls" dialog must list; empty → no dialog. */
  basicControls: readonly BulkBasicControlsCamera[];
}>;

/** A selected row with no model yet and an address its Bridge can probe. */
const identifyTarget = (
  row: Discovery,
  fallbackAgentId: T_Maybe<Agent['id']>
): T_Maybe<{ agentId: Agent['id']; host: string }> => {
  // Iris-enabled cameras name their own model; a known model needs no probe.
  if (row.modelId || row.irisEnabled) return undefined;

  const host = cameraHost(row);
  // The row's own Bridge: private addresses repeat across sites, so another
  // Bridge's probe would identify a different camera.
  const agentId = row.agentId ?? fallbackAgentId;

  return host && agentId ? { agentId, host } : undefined;
};

/**
 * ENG-4940: identify every selected row that has no model, one probe per
 * (Bridge, address). Resolves with the identified row per selected row id.
 * Rows sharing an address share the one probe (the same camera, advertised
 * twice). Nothing is probed beyond the selected rows' own addresses.
 */
export const identifySelectedRows = async ({
  rows,
  fallbackAgentId,
  identify
}: {
  rows: readonly Discovery[];
  fallbackAgentId: T_Maybe<Agent['id']>;
  identify: (agentId: Agent['id'], host: string) => Promise<T_Maybe<IdentifiedCamera>>;
}): Promise<Map<Discovery['id'], IdentifiedCamera>> => {
  const probes = new Map<string, Promise<T_Maybe<IdentifiedCamera>>>();
  const rowProbes: [Discovery['id'], Promise<T_Maybe<IdentifiedCamera>>][] = [];

  for (const row of rows) {
    const target = identifyTarget(row, fallbackAgentId);
    if (!target) continue;

    const key = `${target.agentId}|${target.host.toLowerCase()}`;
    let probe = probes.get(key);
    if (!probe) {
      // A failed probe reads as "not identified": the save must never wait on,
      // or fail because of, the identify step.
      probe = identify(target.agentId, target.host).catch(() => undefined);
      probes.set(key, probe);
    }
    rowProbes.push([row.id, probe]);
  }

  const identified = new Map<Discovery['id'], IdentifiedCamera>();
  for (const [id, probe] of rowProbes) {
    const hit = await probe;
    if (hit) identified.set(id, hit);
  }

  return identified;
};

/**
 * A row as the save should see it once identified: the identified model (and
 * brand, when the match named one) replaces what discovery had. Never touches
 * the row's address, transport or Bridge.
 */
const applyIdentified = (row: Discovery, hit: T_Maybe<IdentifiedCamera>): Discovery =>
  hit ? ({ ...row, modelId: hit.modelId, brandId: hit.brandId ?? row.brandId } as Discovery) : row;

/**
 * ENG-4942: the selected rows that will be saved on the generic driver, in
 * selection order — the multi-add counterpart of `shouldShowBasicControls`
 * (identification.slice.ts), which shows D1 when the saved model is `generic`:
 *
 *   - a model whose template says `generic` — a brand's "Other" model, or a
 *     not-yet-supported model (the registry marks those `generic` too, and they
 *     are named as such);
 *   - a brand-only match: saved on that brand's generic model (or, while that
 *     resolves, on no model), so on the generic driver either way.
 *
 *   - a row nothing identified that the save still gives camera controls (the
 *     default VISCA/ONVIF control of `buildBulkCandidate`): listed as "This
 *     camera" with blank brand and model.
 *
 * Not listed: a model with its own driver, a model whose template has not
 * resolved (its driver is unknown), an Iris camera, and a source the save
 * gives no controls at all (a local capture device).
 */
export const listBasicControlsCameras = ({
  selectionIds,
  discoveries,
  templates,
  brandDetected,
  brands
}: {
  selectionIds: readonly Discovery['id'][];
  discoveries: readonly Discovery[];
  templates: T_Maybe<BulkTemplates>;
  brandDetected: T_Maybe<BulkBrandDetected>;
  brands: T_Maybe<Collection<Brand>>;
}): BulkBasicControlsCamera[] => {
  const byId = new Map(discoveries.map((row) => [row.id, row]));
  const brandName = (id: T_Maybe<Brand['id']>): string =>
    (id ? brands?.items[id]?.name : undefined) ?? '';
  const listed: BulkBasicControlsCamera[] = [];

  for (const id of new Set(selectionIds)) {
    const row = byId.get(id);
    if (!row) continue;

    let names: T_Maybe<{ brandName: string; modelName: string; notYetSupported: boolean }>;

    if (row.modelId) {
      const template = templates?.[row.modelId];
      // A template for another model says nothing about this one.
      const model = template?.model.id === row.modelId ? template.model : undefined;
      if (model?.generic) {
        names = {
          brandName: brandName(model.brandId ?? row.brandId),
          modelName: model.name,
          notYetSupported: model.notYetSupported === true
        };
      }
    } else if (row.brandId) {
      names = {
        brandName: brandName(row.brandId),
        modelName: brandDetected?.[row.brandId]?.template?.model.name ?? '',
        notYetSupported: false
      };
    } else if (!row.irisEnabled && buildBulkCandidate(row)?.control) {
      names = { brandName: '', modelName: '', notYetSupported: false };
    }

    if (!names) continue;

    listed.push({
      id: row.id,
      name: row.name,
      // A brand's generic model is that brand's catch-all, whatever the registry
      // calls it ("Other", "Generic PTZ", …): it reads "<Brand> camera". Only a
      // named, not-yet-supported model is shown by its model name.
      label: basicControlsCameraLabel({
        brandName: names.brandName,
        modelName: names.notYetSupported ? names.modelName : ''
      }),
      ...names
    });
  }

  return listed;
};

/**
 * ENG-4940 then ENG-4942: identify the model-less selected rows, fetch any
 * selected model's missing template (the driver decision needs it; the save
 * would fetch it anyway), then list the basic-controls cameras.
 *
 * A template that fails to load leaves its row unlisted and is fetched again by
 * the save (`runBulkCommit`), which owns that failure.
 */
export const prepareBulkSave = async ({
  selectionIds,
  discoveries,
  templates,
  brandDetected,
  brands,
  fallbackAgentId,
  identify,
  fetchTemplate
}: {
  selectionIds: readonly Discovery['id'][];
  discoveries: readonly Discovery[];
  templates: T_Maybe<BulkTemplates>;
  brandDetected: T_Maybe<BulkBrandDetected>;
  brands: T_Maybe<Collection<Brand>>;
  fallbackAgentId: T_Maybe<Agent['id']>;
  identify: (agentId: Agent['id'], host: string) => Promise<T_Maybe<IdentifiedCamera>>;
  fetchTemplate: (modelId: Model['id']) => Promise<T_Maybe<Template>>;
}): Promise<BulkSavePrepared> => {
  const selected = new Set(selectionIds);
  const identified = await identifySelectedRows({
    rows: discoveries.filter((row) => selected.has(row.id)),
    fallbackAgentId,
    identify
  });

  const prepared = discoveries.map((row) => applyIdentified(row, identified.get(row.id)));
  const merged: BulkTemplates = { ...templates };

  const missing = new Set<Model['id']>();
  for (const row of prepared) {
    if (selected.has(row.id) && row.modelId && !merged[row.modelId]) missing.add(row.modelId);
  }
  // Sequential: registry requests share a mutex (see runBulkCommit).
  for (const modelId of missing) {
    try {
      const template = await fetchTemplate(modelId);
      if (template) merged[modelId] = template;
    } catch {
      // Left for the save to fetch again and report.
    }
  }

  return {
    selectionIds: [...selectionIds],
    discoveries: prepared,
    templates: merged,
    basicControls: listBasicControlsCameras({
      selectionIds,
      discoveries: prepared,
      templates: merged,
      brandDetected,
      brands
    })
  };
};
